/**
 * O sorteio de encontros: as ideias do arquivo, e a ideia inédita da IA.
 *
 * É o `date_idea_service.dart` mais o `ai_date_idea_generator.dart`. Como na
 * academia, o serviço não abre o arquivo sozinho: quem monta o `Sorteio`
 * entrega a lista já lida, porque o React Native não tem `fetch` de arquivo
 * relativo e assim o mesmo serviço serve aos dois aparelhos.
 */

import { traduzir } from '../i18n/idioma';
import { ligacao } from '../nucleo/ligacao';
import type { Formato } from '../types/casa';
import type { IdeiaDeEncontro } from './ideia';

export class Sorteio {
  constructor(private readonly banco: ReadonlyArray<IdeiaDeEncontro>) {}

  /**
   * Uma casa em fusos diferentes não quer ouvir "vão à praia juntos".
   *
   * O `formato` vem da casa da sessão. Sem casa configurada o padrão é o mesmo
   * do Flutter: trata como quem mora perto, que é o caso da maioria.
   */
  cabe(onde: IdeiaDeEncontro['onde'], formato?: Formato): boolean {
    if (formato !== 'longe') return onde !== 'longe';
    return onde === 'longe' || onde === 'ambos';
  }

  /** As que cabem nesta casa, na ordem do arquivo. */
  disponiveis(formato?: Formato): IdeiaDeEncontro[] {
    return this.banco.filter((i) => this.cabe(i.onde, formato));
  }

  /**
   * Uma ideia ao acaso. `evitar` é a que já está na tela: sortear a mesma duas
   * vezes seguidas parece que o botão não funcionou.
   */
  sortear(formato?: Formato, evitar?: string): IdeiaDeEncontro | null {
    const cabem = this.disponiveis(formato);
    if (cabem.length === 0) return null;
    const outras = evitar ? cabem.filter((i) => i.id !== evitar) : cabem;
    const lista = outras.length > 0 ? outras : cabem;
    return lista[Math.floor(Math.random() * lista.length)];
  }
}

/** O que a IA precisa saber para não escrever um encontro genérico. */
export interface ContextoDoEncontro {
  humor?: string;
  hora?: string;
  interesses?: string;
}

/**
 * Este pedido não passa pela persona do Kovu: mandar `systemPrompt` a
 * substitui, e com ela some a linha que diz em que língua responder. Por isso
 * a língua é resolvida aqui, e pelo próprio dicionário — a frase inteira é o
 * verbete, então o modelo recebe um pedido coerente em vez de um português com
 * a palavra "inglês" no meio.
 */
const papel = () =>
  traduzir(
    'Você é uma planejadora criativa de encontros. Crie títulos de encontros ' +
      'românticos e originais em português do Brasil. Responda APENAS com o título ' +
      '— sem aspas, sem markdown, sem mais nada.',
  );

/**
 * A ideia que não estava no arquivo — o `AiDateIdeaGenerator`.
 *
 * O pedido é o de lá, linha por linha: as três pistas só entram quando existem,
 * e o fecho repete "APENAS o título" porque sem ele o modelo devolve uma
 * explicação junto e o cartão fica com um parágrafo dentro.
 *
 * Devolve `null` tanto quando o modelo não responde nada aproveitável quanto
 * quando o pedido nem chega a sair — é o mesmo `catch` do `AiDateIdeaGenerator`,
 * e é o que deixa o cartão cair no sorteio do arquivo sem saber por quê.
 */
export async function idealizarEncontro(
  contexto: ContextoDoEncontro = {},
): Promise<IdeiaDeEncontro | null> {
  const linhas = [
    traduzir(
      'Crie UM título de ideia de encontro romântico e original (curto, de 3 a 7 palavras). Seja criativa e pessoal.',
    ),
  ];
  if (contexto.humor) linhas.push(traduzir('Humor de agora: {v}', { v: contexto.humor }));
  if (contexto.hora) linhas.push(traduzir('Quando: {v}', { v: contexto.hora }));
  if (contexto.interesses) linhas.push(traduzir('Eles curtem: {v}', { v: contexto.interesses }));
  linhas.push('');
  linhas.push(
    traduzir(
      'Responda APENAS com o título, em português do Brasil — sem aspas, sem explicação, sem mais nada.',
    ),
  );

  try {
    const resposta = await ligacao().api.kovu([{ role: 'user', content: linhas.join('\n') }], {
      systemPrompt: papel(),
    });
    const titulo = resposta.trim().replace(/["']/g, '').trim();
    if (!titulo) return null;
    return { id: `ia_${Date.now()}`, titulo, onde: 'ambos' };
  } catch {
    return null;
  }
}
